import { useEffect, useMemo, useState } from 'react';
import { VOICE_BARS } from './FooterVoice.constants';

const waveformCache = new Map();
const MIN_BAR_HEIGHT = 8;
const MAX_BAR_HEIGHT = 88;
const FLAT_BAR_HEIGHT = 8;
const SILENCE_PEAK_THRESHOLD = 0.006;
const LOW_LEVEL_PEAK_THRESHOLD = 0.04;
const LOW_LEVEL_DYNAMIC_RANGE = 0.012;

const areWaveformBarsEqual = (firstBars, secondBars) => (
    firstBars.length === secondBars.length &&
    firstBars.every((height, index) => height === secondBars[index])
);

export const flatWaveformBars = (barCount) => Array.from({ length: barCount }, () => FLAT_BAR_HEIGHT);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const percentile = (values, ratio) => {
    if (!values.length) return 0;

    const sortedValues = [...values].sort((first, second) => first - second);
    const exactIndex = (sortedValues.length - 1) * ratio;
    const lowerIndex = Math.floor(exactIndex);
    const upperIndex = Math.ceil(exactIndex);
    const weight = exactIndex - lowerIndex;

    if (lowerIndex === upperIndex) return sortedValues[lowerIndex];
    return sortedValues[lowerIndex] * (1 - weight) + sortedValues[upperIndex] * weight;
};

const getVisibleMaxHeight = (peakCeiling) => {
    if (peakCeiling <= 0.02) return 34;
    if (peakCeiling >= 0.08) return MAX_BAR_HEIGHT;

    return 34 + ((peakCeiling - 0.02) / 0.06) * (MAX_BAR_HEIGHT - 34);
};

const smoothBars = (bars) => {
    if (bars.length < 3) return bars;

    return bars.map((height, index) => {
        const previous = bars[Math.max(0, index - 1)];
        const next = bars[Math.min(bars.length - 1, index + 1)];
        return Math.round(previous * 0.18 + height * 0.64 + next * 0.18);
    });
};

export const waveformPeaksToBars = (peaks = [], maxHeight = MAX_BAR_HEIGHT) => {
    if (!Array.isArray(peaks) || peaks.length === 0) {
        return [];
    }

    return smoothBars(peaks.map((peak) => {
        const normalized = clamp(Number(peak) || 0, 0, 1);
        const shaped = Math.pow(normalized, 0.58);
        if (shaped <= 0.015) return FLAT_BAR_HEIGHT;

        return Math.round(MIN_BAR_HEIGHT + shaped * (maxHeight - MIN_BAR_HEIGHT));
    }));
};

export const normalizeWaveformPeaks = (peaks) => {
    if (!peaks.length) return [];

    const lowPeak = percentile(peaks, 0.1);
    const medianPeak = percentile(peaks, 0.5);
    const peakCeiling = percentile(peaks, 0.98);
    const dynamicRange = peakCeiling - lowPeak;

    if (peakCeiling < SILENCE_PEAK_THRESHOLD) {
        return flatWaveformBars(peaks.length);
    }

    if (peakCeiling < LOW_LEVEL_PEAK_THRESHOLD && dynamicRange < LOW_LEVEL_DYNAMIC_RANGE) {
        return flatWaveformBars(peaks.length);
    }

    if (dynamicRange < Math.max(0.003, peakCeiling * 0.12)) {
        return flatWaveformBars(peaks.length);
    }

    const noiseFloor = Math.min(
        peakCeiling * 0.75,
        Math.max(SILENCE_PEAK_THRESHOLD * 0.75, lowPeak, medianPeak * 0.55)
    );
    const usableRange = peakCeiling - noiseFloor;

    if (usableRange <= 0.003) {
        return flatWaveformBars(peaks.length);
    }

    const visibleMaxHeight = getVisibleMaxHeight(peakCeiling);

    return waveformPeaksToBars(
        peaks.map((peak) => clamp((peak - noiseFloor) / usableRange, 0, 1)),
        visibleMaxHeight
    );
};

export const buildWaveformBars = (audioBuffer, barCount) => {
    const channels = Array.from({ length: audioBuffer.numberOfChannels }, (_, index) => (
        audioBuffer.getChannelData(index)
    ));
    const totalSamples = audioBuffer.length;
    const samplesPerBar = Math.max(1, Math.floor(totalSamples / barCount));
    const peaks = [];

    for (let barIndex = 0; barIndex < barCount; barIndex += 1) {
        const start = barIndex * samplesPerBar;
        const end = barIndex === barCount - 1
            ? totalSamples
            : Math.min(totalSamples, start + samplesPerBar);
        let sum = 0;
        let maxSample = 0;
        let sampleCount = 0;

        for (let sampleIndex = start; sampleIndex < end; sampleIndex += 1) {
            let channelTotal = 0;
            let channelPeak = 0;
            channels.forEach((channel) => {
                const sample = Math.abs(channel[sampleIndex] || 0);
                channelTotal += sample * sample;
                channelPeak = Math.max(channelPeak, sample);
            });
            sum += channelTotal / channels.length;
            maxSample = Math.max(maxSample, channelPeak);
            sampleCount += 1;
        }

        const rms = sampleCount ? Math.sqrt(sum / sampleCount) : 0;
        peaks.push((maxSample * 0.72) + (rms * 0.28));
    }

    return normalizeWaveformPeaks(peaks);
};

const decodeWaveform = async (url, barCount) => {
    if (!url || typeof window === 'undefined') return flatWaveformBars(barCount);

    const cacheKey = `${url}:${barCount}`;
    if (!waveformCache.has(cacheKey)) {
        waveformCache.set(cacheKey, (async () => {
            const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextCtor || typeof fetch !== 'function') return flatWaveformBars(barCount);

            let audioContext;
            try {
                const response = await fetch(url);
                const audioData = await response.arrayBuffer();
                audioContext = new AudioContextCtor();
                const audioBuffer = await audioContext.decodeAudioData(audioData);
                return buildWaveformBars(audioBuffer, barCount);
            } catch (error) {
                return flatWaveformBars(barCount);
            } finally {
                if (audioContext && typeof audioContext.close === 'function') {
                    audioContext.close().catch(() => {});
                }
            }
        })());
    }

    return waveformCache.get(cacheKey);
};

const useAudioWaveform = (url, barCount = VOICE_BARS.length) => {
    const fallbackBars = useMemo(() => flatWaveformBars(barCount), [barCount]);
    const [bars, setBars] = useState(fallbackBars);

    useEffect(() => {
        let isMounted = true;
        setBars(fallbackBars);

        decodeWaveform(url, barCount).then((nextBars) => {
            const nextWaveformBars = nextBars.slice(0, barCount);
            if (isMounted && !areWaveformBarsEqual(fallbackBars, nextWaveformBars)) {
                setBars(nextWaveformBars);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [url, barCount, fallbackBars]);

    return bars;
};

export default useAudioWaveform;
